import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ArrowLeft, TrendingUp, Download } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const money = (n) => "$" + Math.round(n || 0).toLocaleString();
const METRICS = [["deals", "Deals created", (v) => v], ["won", "Won value", money], ["clients", "New clients", (v) => v]];

export default function CompaniesReport() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState("deals");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/companies/report").then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); navigate("/companies"); }); }, []);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.months.map((mo, i) => {
      const row = { month: mo.slice(5) + "/" + mo.slice(2, 4) };
      data.companies.forEach((c) => { row[c.name] = (data.series[c.id]?.[metric] || [])[i] || 0; });
      return row;
    });
  }, [data, metric]);

  if (!data) return <div className="text-sm text-slate-400 p-8">Loading…</div>;
  const fmt = METRICS.find((m) => m[0] === metric)[2];
  const empty = data.companies.length === 0;

  const downloadCsv = () => {
    const esc = (x) => `"${String(x ?? "").replace(/"/g, '""')}"`;
    const rows = [["Company", "Status", "Clients", "Open pipeline", "Open deals", "Open work", "Overdue", "Users"]];
    data.companies.forEach((c) => rows.push([c.name, c.status, c.clients, c.pipeline_value, c.open_deals, c.open_work, c.overdue, c.users]));
    rows.push([]);
    rows.push([`Trend (${metric})`, "", ...data.months]);
    data.companies.forEach((c) => rows.push([c.name, "", ...(data.series[c.id]?.[metric] || [])]));
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "portfolio-report.csv"; a.click(); URL.revokeObjectURL(a.href);
    toast.success("CSV downloaded");
  };

  const downloadPdf = () => {
    const comp = data.companies.map((c) => `<tr><td>${c.name}</td><td>${c.status}</td><td>${c.clients}</td><td>${money(c.pipeline_value)}</td><td>${c.open_deals}</td><td>${c.open_work}</td><td>${c.overdue}</td><td>${c.users}</td></tr>`).join("");
    const trendHead = data.months.map((m) => `<th>${m}</th>`).join("");
    const trend = data.companies.map((c) => `<tr><td>${c.name}</td>${(data.series[c.id]?.[metric] || []).map((v) => `<td>${metric === "won" ? money(v) : v}</td>`).join("")}</tr>`).join("");
    const html = `<html><head><title>Portfolio report</title><style>body{font-family:system-ui,sans-serif;padding:32px;color:#1e293b}h1{font-size:22px;margin:0}h2{font-size:13px;margin-top:26px;text-transform:uppercase;letter-spacing:.08em;color:#64748b}table{width:100%;border-collapse:collapse;margin-top:8px;font-size:12px}th,td{border:1px solid #e2e8f0;padding:6px 8px;text-align:left}th{background:#f8fafc}</style></head><body><h1>Portfolio report</h1><p style="color:#64748b;font-size:12px">Generated ${new Date().toLocaleString()}</p><h2>Comparison</h2><table><thead><tr><th>Company</th><th>Status</th><th>Clients</th><th>Open pipeline</th><th>Open deals</th><th>Open work</th><th>Overdue</th><th>Users</th></tr></thead><tbody>${comp}</tbody></table><h2>Trend — ${metric} (last 6 months)</h2><table><thead><tr><th>Company</th>${trendHead}</tr></thead><tbody>${trend}</tbody></table></body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    const w = window.open(url, "_blank");
    if (!w) { URL.revokeObjectURL(url); toast.error("Allow pop-ups to export PDF"); return; }
    w.addEventListener("load", () => { w.focus(); w.print(); });
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <div data-testid="companies-report-page">
      <button onClick={() => navigate("/companies")} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-2" data-testid="report-back"><ArrowLeft className="w-3.5 h-3.5" /> Back to Companies</button>
      <PageHeader title="Portfolio reporting" subtitle="Compare your companies and see how they trend — visible only to you" />
      {empty ? <div className="panel p-12 text-center text-sm text-slate-400">Create companies to see portfolio comparisons and trends.</div> : (
        <>
          <div className="panel p-5 mb-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="eyebrow flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Last 6 months</p>
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg border p-0.5 text-xs" style={{ borderColor: "var(--line)" }}>
                  {METRICS.map(([k, l]) => <button key={k} onClick={() => setMetric(k)} className={cn("px-3 py-1 rounded-md transition-colors", metric === k ? "bg-slate-900 text-white" : "text-slate-500")} data-testid={`report-metric-${k}`}>{l}</button>)}
                </div>
                <DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="rounded-full" data-testid="report-export"><Download className="w-3.5 h-3.5 mr-1" /> Export</Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={downloadCsv} data-testid="report-export-csv">Download CSV</DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadPdf} data-testid="report-export-pdf">Download PDF</DropdownMenuItem>
                  </DropdownMenuContent></DropdownMenu>
              </div>
            </div>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => metric === "won" ? "$" + (v >= 1000 ? (v / 1000) + "k" : v) : v} width={52} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {data.companies.map((c) => <Line key={c.id} type="monotone" dataKey={c.name} stroke={c.accent || "#4f46e5"} strokeWidth={2} dot={false} />)}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="panel p-0 overflow-x-auto" data-testid="report-compare-table">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b" style={{ borderColor: "var(--line)" }}>
                <th className="p-3 font-medium">Company</th><th className="p-3 font-medium">Clients</th><th className="p-3 font-medium">Open pipeline</th><th className="p-3 font-medium">Open deals</th><th className="p-3 font-medium">Open work</th><th className="p-3 font-medium">Overdue</th><th className="p-3 font-medium">Users</th>
              </tr></thead>
              <tbody>{data.companies.map((c) => (
                <tr key={c.id} className="border-b last:border-0" style={{ borderColor: "var(--line)" }} data-testid={`report-row-${c.id}`}>
                  <td className="p-3"><span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c.accent }} />{c.name}{c.status !== "active" && <span className="chip border-slate-200 text-slate-500 bg-slate-50">{c.status}</span>}</span></td>
                  <td className="p-3 tabular">{c.clients}</td><td className="p-3 tabular">{money(c.pipeline_value)}</td><td className="p-3 tabular">{c.open_deals}</td><td className="p-3 tabular">{c.open_work}</td><td className={cn("p-3 tabular", c.overdue && "text-amber-600 font-medium")}>{c.overdue}</td><td className="p-3 tabular">{c.users}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
